/**
 * @fileoverview Bids Tab Component
 * 
 * Comprehensive bid management interface for RFQs. Displays all submitted bids with:
 * - Sortable columns (price, lead time, match score)
 * - Vendor compliance checking
 * - Match score calculation and display
 * - Line item breakdowns
 * - Bid awarding workflow
 * - Vendor summary modals
 * 
 * Supports both client and vendor views with appropriate permissions and visibility.
 * 
 * @module components/jobs/BidsTab
 */

import { useState, useEffect, useCallback, Fragment } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useBids } from '@/hooks/useBids';
import { usePermissions } from '@/hooks/usePermissions';
import { formatCurrency, VendorMatchScore } from '@/lib/types/job';
import { CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronUp, Eye, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { VendorSummaryModal } from '@/components/vendor/VendorSummaryModal';
import { AwardedBidSummary } from './AwardedBidSummary';
import { useReviews } from '@/hooks/useReviews';
import { supabase } from '@/integrations/supabase/client';
import { MatchScoreDisplay } from '@/components/rfq/MatchScoreDisplay';
import { BidLineItemsView } from '@/components/jobs/BidLineItemsView';
import { AwardSelectionModal } from '@/components/jobs/AwardSelectionModal';
import { useBidLineItems } from '@/hooks/useBidLineItems';
import { useJobSpecifications, useRFQSpecificationQuantities } from '@/hooks/useJobSpecifications';
import { useSpecificationLocationQuantities } from '@/hooks/useSpecificationLocationQuantities';
import { getVendorDisplayName } from '@/utils/vendorHelpers';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

/**
 * Props for the BidsTab component
 */
interface BidsTabProps {
  /** RFQ ID to fetch bids for */
  rfqId: string;
  /** Current job status (e.g., 'rfq_open', 'awarded') */
  jobStatus: string;
  /** Whether the current user is a client (true) or vendor (false) */
  isClient: boolean;
  /** Job ID for fetching specifications and delivery locations */
  jobId: string;
}

/**
 * BidsTab - Comprehensive bid management interface
 * 
 * Displays all bids submitted for an RFQ with advanced features:
 * - **Sorting**: Sort by price, lead time, or match score
 * - **Match Scores**: AI-calculated vendor suitability scores (clients only)
 * - **Compliance Checking**: Validates vendor profiles before allowing awards
 * - **Line Items**: Expandable detailed pricing breakdowns
 * - **Award Workflow**: Multi-step bid selection and awarding process
 * - **Vendor Summaries**: Quick vendor profile previews
 * 
 * **Client View**: See all bids, compare vendors, award contracts
 * **Vendor View**: See only your own bid
 * 
 * @component
 * @example
 * ```tsx
 * <BidsTab 
 *   rfqId="rfq-123"
 *   jobStatus="rfq_open"
 *   isClient={true}
 *   jobId="job-456"
 * />
 * ```
 */
export const BidsTab = ({ rfqId, jobStatus, isClient, jobId }: BidsTabProps) => {
  const { bids, loading, error, awardBid } = useBids(rfqId, isClient);
  const { canAwardJob } = usePermissions();
  const { getUserReviewAggregate, getUserReviews } = useReviews();
  const [selectedBid, setSelectedBid] = useState<any>(null);
  const [awardDialog, setAwardDialog] = useState(false);
  const [showAwardSelectionModal, setShowAwardSelectionModal] = useState(false);
  const [awardFormData, setAwardFormData] = useState({ award_notes: '' });
  const [expandedCompliance, setExpandedCompliance] = useState<Set<string>>(new Set());
  const [expandedLineItems, setExpandedLineItems] = useState<Set<string>>(new Set());
  const [showVendorSummary, setShowVendorSummary] = useState(false);
  const [selectedVendorData, setSelectedVendorData] = useState<any>(null);
  const [bidMatchScores, setBidMatchScores] = useState<Record<string, VendorMatchScore>>({});
  const [matchScoresLoading, setMatchScoresLoading] = useState(false);
  const [deliveryLocations, setDeliveryLocations] = useState<any[]>([]);
  const [jobCreatorId, setJobCreatorId] = useState<string | null>(null);
  const [awardData, setAwardData] = useState<any>(null);
  const [awardSelections, setAwardSelections] = useState<any[]>([]);
  const [winningBidOverride, setWinningBidOverride] = useState<any | null>(null);

  // Fetch job specifications, quantity options, and location quantities
  const { specifications, loading: specsLoading } = useJobSpecifications(jobId);
  const { quantities: quantityOptions, loading: quantitiesLoading } = useRFQSpecificationQuantities(rfqId);
  const { matrix: percentageMatrix, loading: matrixLoading } = useSpecificationLocationQuantities(jobId);
  
  // Transform percentageMatrix to simple numeric format
  
  // Fetch job creator for permission checks
  useEffect(() => {
    const fetchJobCreator = async () => {
      const { data } = await supabase
        .from('jobs')
        .select('created_by_user_id')
        .eq('id', jobId)
        .single() as any;
      if (data) setJobCreatorId(data.created_by_user_id);
    };
    if (jobId) fetchJobCreator();
  }, [jobId]);
  // Use the full matrix with both quantity and percentage data for the award modal
  const distributionMatrixForAward = percentageMatrix;
  
  // Store line items for each bid
  const [bidLineItems, setBidLineItems] = useState<Record<string, any[]>>({});

  type SortField = 'price' | 'lead_time' | 'match_score';
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('match_score');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Fetch delivery locations
  useEffect(() => {
    const fetchDeliveryLocations = async () => {
      if (!jobId) return;
      
      try {
        // Only fetch product destinations (exclude sample-only locations)
        const { data, error } = await supabase
          .from('job_delivery_details')
          .select('*')
          .eq('job_id', jobId)
          .eq('is_product_destination', true)
          .order('delivery_order', { ascending: true });
        
        if (error) throw error;
        setDeliveryLocations(data || []);
      } catch (error) {
        console.error('Error fetching delivery locations:', error);
      }
    };
    
    fetchDeliveryLocations();
  }, [jobId]);

  // Fetch award data when job is awarded
  const fetchAwardData = useCallback(async () => {
    if (!jobId) return;
    
    try {
      // Explicit column selection for defensive coding
      const { data: award, error: awardError } = await supabase
        .from('awards')
        .select('id, job_id, winning_bid_id, total_award_amount_cents, awarded_at, award_notes')
        .eq('job_id', jobId)
        .maybeSingle();
      
      if (awardError) {
        console.error('Error fetching award:', awardError);
        toast.error('Failed to load award information');
        return;
      }
      if (!award) return;
      
      setAwardData(award);
      
      const { data: selections, error: selectionsError } = await supabase
        .from('award_selections')
        .select('*')
        .eq('award_id', award.id)
        .order('specification_id');
      
      if (selectionsError) {
        console.error('Error fetching selections:', selectionsError);
        toast.error('Failed to load award selections');
        return;
      }
      setAwardSelections(selections || []);
    } catch (err) {
      console.error('Error fetching award data:', err);
      toast.error('An error occurred loading award data');
    }
  }, [jobId]);

  useEffect(() => {
    if (jobStatus !== 'rfq_open') {
      fetchAwardData();
    }
  }, [jobStatus, fetchAwardData]);

  // Fetch winning bid if not in bids array (vendor view scenario)
  useEffect(() => {
    const loadWinningBidIfMissing = async () => {
      if (!awardData || !awardData.winning_bid_id) return;
      
      const bidExists = bids.some(b => b.id === awardData.winning_bid_id);
      if (!bidExists) {
        try {
          // Fetch bid and vendor profile separately (no FK constraint exists)
          const { data: bidData, error: bidError } = await supabase
            .from('bids')
            .select('*')
            .eq('id', awardData.winning_bid_id)
            .single();
          
          if (!bidError && bidData) {
            // Fetch vendor profile separately and merge
            const { data: vendorProfile } = await supabase
              .from('vendor_profiles_enhanced')
              .select('*, organization:organizations!vendor_profiles_enhanced_organization_id_fkey(id, name)')
              .eq('user_id', bidData.vendor_user_id)
              .maybeSingle();
            
            setWinningBidOverride({ ...bidData, vendor_profile: vendorProfile || null });
          }
        } catch (err) {
          console.error('Error fetching winning bid:', err);
        }
      } else {
        setWinningBidOverride(null);
      }
    };
    
    loadWinningBidIfMissing();
  }, [awardData, bids]);

  // Fetch line items for each bid (with tax calculations)
  useEffect(() => {
    const fetchBidLineItems = async () => {
      if (!bids.length) return;
      
      try {
        const lineItemsMap: Record<string, any[]> = {};
        
        await Promise.all(
          bids.map(async (bid) => {
            // Fetch line items and tax calculations in parallel
            const [lineItemsResult, taxCalcsResult] = await Promise.all([
              supabase
                .from('bid_line_items')
                .select(`
                  *,
                  origin_location:vendor_locations!origin_location_id(
                    id,
                    location_name,
                    city,
                    state
                  )
                `)
                .eq('bid_id', bid.id)
                .order('created_at', { ascending: true }),
              supabase
                .from('bid_line_item_tax_calculations')
                .select('bid_line_item_id, vendor_tax_cents')
                .eq('bid_id', bid.id)
            ]);
            
            if (!lineItemsResult.error && lineItemsResult.data) {
              // Aggregate tax amounts per line item (sum all destination taxes)
              const taxByLineItem = (taxCalcsResult.data || []).reduce((acc, calc) => {
                const key = calc.bid_line_item_id;
                acc[key] = (acc[key] || 0) + (calc.vendor_tax_cents || 0);
                return acc;
              }, {} as Record<string, number>);

              const lineItemsWithTax = lineItemsResult.data.map(item => ({
                ...item,
                tax_cents: taxByLineItem[item.id] || 0
              }));
              lineItemsMap[bid.id] = lineItemsWithTax;
            }
          })
        );
        
        setBidLineItems(lineItemsMap);
      } catch (error) {
        console.error('Error fetching bid line items:', error);
      }
    };
    
    fetchBidLineItems();
  }, [bids]);

  // Calculate total price for a bid from its line items
  const calculateBidTotal = (bidId: string): number => {
    const items = bidLineItems[bidId] || [];
    return items.reduce((sum, item) => sum + item.total_price_cents, 0);
  };

  // Get price range for a bid (min-max across different quantities)
  const getBidPriceRange = (bidId: string): { min: number; max: number; total: number } => {
    const items = bidLineItems[bidId] || [];
    if (items.length === 0) return { min: 0, max: 0, total: 0 };
    
    const prices = items.map(item => item.total_price_cents);
    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
      total: prices.reduce((sum, p) => sum + p, 0)
    };
  };

  // Get lead time from bid line items
  const getBidLeadTime = (bidId: string): number => {
    const items = bidLineItems[bidId] || [];
    if (items.length === 0) return 0;
    
    // Get all unique lead times
    const leadTimes = items.map(item => item.lead_time_days);
    const uniqueLeadTimes = [...new Set(leadTimes)];
    
    // If all lead times are the same, return that value
    // Otherwise return the minimum (most optimistic delivery time)
    return uniqueLeadTimes.length === 1 ? uniqueLeadTimes[0] : Math.min(...leadTimes);
  };

  // Fetch match scores for all bids
  useEffect(() => {
    const fetchBidMatchScores = async () => {
      if (!rfqId || bids.length === 0 || !isClient) return;
      
      setMatchScoresLoading(true);
      try {
        const scores: Record<string, VendorMatchScore> = {};
        
        await Promise.all(
          bids.map(async (bid) => {
            const { data, error } = await supabase.rpc(
              'calculate_vendor_match_score' as any,
              {
                p_vendor_user_id: bid.vendor_user_id,
                p_rfq_id: rfqId
              } as any
            );
            
            if (!error && data) {
              scores[bid.id] = data as any;
            }
          })
        );
        
        setBidMatchScores(scores);
      } catch (error) {
        console.error('Error fetching bid match scores:', error);
      } finally {
        setMatchScoresLoading(false);
      }
    };

    fetchBidMatchScores();
  }, [bids, rfqId, isClient]);

  // Sorting logic
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'match_score' ? 'desc' : 'asc');
    }
  };

  const sortedBids = [...bids].sort((a, b) => {
    let comparison = 0;
    
    switch (sortField) {
      case 'price':
        // Sort by minimum line item price
        const itemsA = bidLineItems[a.id] || [];
        const itemsB = bidLineItems[b.id] || [];
        const minPriceA = itemsA.length > 0 ? Math.min(...itemsA.map(item => item.total_price_cents)) : 0;
        const minPriceB = itemsB.length > 0 ? Math.min(...itemsB.map(item => item.total_price_cents)) : 0;
        comparison = minPriceA - minPriceB;
        break;
      case 'lead_time':
        const leadTimeA = getBidLeadTime(a.id);
        const leadTimeB = getBidLeadTime(b.id);
        comparison = leadTimeA - leadTimeB;
        break;
      case 'match_score':
        const scoreA = bidMatchScores[a.id]?.score ?? -1;
        const scoreB = bidMatchScores[b.id]?.score ?? -1;
        comparison = scoreA - scoreB;
        break;
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  if (loading) return <div>Loading bids...</div>;
  if (error) return <div>Error loading bids: {error}</div>;

  const isAwarded = jobStatus === 'awarded';
  const canAward = isClient && !isAwarded && jobStatus === 'rfq_open' && canAwardJob(jobCreatorId || undefined);

  const getBidSummary = () => {
    if (bids.length === 0) return null;
    
    const eligibleBids = bids.filter(bid => {
      const compliance = checkVendorCompliance(bid.vendor_profile);
      return compliance.isCompliant && bid.status === 'submitted';
    });

    // Get all individual line item prices across all bids
    const allLineItemPrices: number[] = [];
    bids.forEach(bid => {
      const items = bidLineItems[bid.id] || [];
      items.forEach(item => {
        allLineItemPrices.push(item.total_price_cents);
      });
    });
    
    if (allLineItemPrices.length === 0) {
      return {
        totalBids: bids.length,
        eligibleBids: eligibleBids.length,
        priceRange: { min: 0, max: 0, avg: 0 }
      };
    }

    const minPrice = Math.min(...allLineItemPrices);
    const maxPrice = Math.max(...allLineItemPrices);
    
    // Calculate average based on bid totals (not individual line items)
    const bidTotals = bids.map(bid => calculateBidTotal(bid.id)).filter(p => p > 0);
    const avgPrice = bidTotals.length > 0 ? bidTotals.reduce((a, b) => a + b, 0) / bidTotals.length : 0;

    return {
      totalBids: bids.length,
      eligibleBids: eligibleBids.length,
      priceRange: {
        min: minPrice,
        max: maxPrice,
        avg: avgPrice
      }
    };
  };

  const checkVendorCompliance = (vendorProfile: any) => {
    console.log('checkVendorCompliance - vendorProfile:', vendorProfile);
    
    if (!vendorProfile) {
      console.log('checkVendorCompliance - No vendor profile found');
      return { isCompliant: false, issues: ['No vendor profile'] };
    }

    const issues = [];
    
    // Check verification status if available
    if (vendorProfile.is_verified === false) issues.push('Profile not verified');

    console.log('checkVendorCompliance - Issues found:', issues);

    return {
      isCompliant: issues.length === 0,
      issues
    };
  };

  const handleAwardClick = (bid: any) => {
    const lineItems = bidLineItems[bid.id] || [];
    
    // Validate that bid has line items
    if (lineItems.length === 0) {
      toast.error('This bid has no line items. Cannot award.');
      return;
    }
    
    setSelectedBid(bid);
    setShowAwardSelectionModal(true);
  };

  const toggleLineItems = (bidId: string) => {
    const newExpanded = new Set(expandedLineItems);
    if (newExpanded.has(bidId)) {
      newExpanded.delete(bidId);
    } else {
      newExpanded.add(bidId);
      // Track quote viewed when expanding
      const bid = bids.find(b => b.id === bidId);
      analytics.track({
        name: ANALYTICS_EVENTS.QUOTE_VIEWED,
        category: 'engagement',
        properties: {
          bid_id: bidId,
          rfq_id: rfqId,
          amount_cents: calculateBidTotal(bidId),
        },
      });
    }
    setExpandedLineItems(newExpanded);
  };

  const toggleComplianceDetails = (bidId: string) => {
    const newExpanded = new Set(expandedCompliance);
    if (newExpanded.has(bidId)) {
      newExpanded.delete(bidId);
    } else {
      newExpanded.add(bidId);
    }
    setExpandedCompliance(newExpanded);
  };

  const handleShowVendorSummary = async (bid: any) => {
    try {
      const vendorProfile = bid.vendor_profile;
      if (!vendorProfile?.id) {
        throw new Error('Vendor profile not found');
      }

      const [enrichedRes, reviewAggregate, reviews] = await Promise.all([
        supabase
          .from('vendor_profiles_enhanced')
          .select(`
            *,
            organization:organizations!vendor_profiles_enhanced_organization_id_fkey(
              id,
              name
            ),
            vendor_certifications (
              *,
              certification_type:certification_types (*)
            ),
            vendor_capabilities (
              *,
              capability:capabilities(id, name)
            )
          `)
          .eq('id', vendorProfile.id)
          .maybeSingle(),
        getUserReviewAggregate(bid.vendor_user_id),
        getUserReviews(bid.vendor_user_id)
      ]);

      const { data: enriched, error: enrichError } = enrichedRes as any;
      if (enrichError) {
        console.warn('Failed to load vendor related data', enrichError);
      }

      let certifications = enriched?.vendor_certifications ?? [];
      let capabilities = enriched?.vendor_capabilities ?? [];

      // Fallback if nested fetch returned nothing
      if (!enriched) {
        const [certRes, capRes] = await Promise.all([
          supabase.from('vendor_certifications').select('*, certification_type:certification_types(*)').eq('vendor_profile_id', vendorProfile.id),
          supabase.from('vendor_capabilities').select('*, capability:capabilities(id, name)').eq('vendor_profile_id', vendorProfile.id),
        ]);
        certifications = (certRes as any)?.data || [];
        capabilities = (capRes as any)?.data || [];
      }

      // Create profile with organization name
      const profileWithOrgName = enriched ? {
        ...enriched,
        display_name: enriched.organization?.name || enriched.business_name
      } : {
        ...vendorProfile,
        display_name: vendorProfile.business_name
      };

      setSelectedVendorData({
        profile: profileWithOrgName,
        certifications,
        capabilities,
        reviewAggregate,
        reviews
      });
      setShowVendorSummary(true);
    } catch (error) {
      console.error('Error loading vendor data:', error);
      setSelectedVendorData({
        profile: bid.vendor_profile,
        certifications: [],
        capabilities: [],
        reviewAggregate: null,
        reviews: []
      });
      setShowVendorSummary(true);
    }
  };

  const summary = getBidSummary();

  // Determine winning bid for summary display
  const winningBidForSummary = 
    (awardData && (bids.find(b => b.id === awardData.winning_bid_id) || winningBidOverride)) ||
    bids.find(b => b.status === 'winner') ||
    null;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && jobStatus === 'rfq_open' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Bids</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.totalBids}</div>
              <p className="text-xs text-muted-foreground">
                {summary.eligibleBids} eligible for award
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Price Range</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(summary.priceRange.min)} - {formatCurrency(summary.priceRange.max)}
              </div>
              <p className="text-xs text-muted-foreground">
                Avg: {formatCurrency(summary.priceRange.avg)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Status</CardTitle>
            </CardHeader>
            <CardContent>
              <Badge variant={isAwarded ? 'default' : 'secondary'}>
                {isAwarded ? 'Awarded' : 'Open for Awards'}
              </Badge>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Awarded Bid Summary - Only show if awarded */}
      {awardData && jobStatus !== 'rfq_open' && (() => {
        // Enrich award selections with tax data from winning bid's line items
        const winningBidLineItems = awardData?.winning_bid_id 
          ? bidLineItems[awardData.winning_bid_id] || []
          : [];
        
        const awardSelectionsWithTax = awardSelections.map(selection => {
          const lineItem = winningBidLineItems.find(li => li.id === selection.bid_line_item_id);
          return {
            ...selection,
            tax_cents: lineItem?.tax_cents || 0
          };
        });

        return (
          <AwardedBidSummary
            winningBid={winningBidForSummary}
            awardData={awardData}
            awardSelections={awardSelectionsWithTax}
            specifications={specifications}
            quantityOptions={quantityOptions}
          />
        );
      })()}

      {/* Bids Table */}
      <Card id="submitted-bids" className={bids.length > 0 ? 'border-2 border-primary bg-primary/5 shadow-xl ring-4 ring-primary/30 transition-all duration-300' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                All Submitted Bids
                {bids.length > 0 && (
                  <Badge variant="default" className="ml-2">
                    {bids.length} {bids.length === 1 ? 'Bid' : 'Bids'}
                  </Badge>
                )}
              </CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {bids.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No bids submitted yet.
            </p>
          ) : (
            <div className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isClient && <TableHead className="w-24">Vendor</TableHead>}
                    
                    <TableHead className="w-28">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 -ml-2 hover:bg-transparent"
                        onClick={() => handleSort('price')}
                      >
                        Price
                        {sortField === 'price' && (
                          sortDirection === 'asc' ? 
                            <ChevronUp className="ml-1 h-3 w-3" /> : 
                            <ChevronDown className="ml-1 h-3 w-3" />
                        )}
                      </Button>
                    </TableHead>
                    
                    <TableHead className="w-20">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 -ml-2 hover:bg-transparent"
                        onClick={() => handleSort('lead_time')}
                      >
                        Lead Time
                        {sortField === 'lead_time' && (
                          sortDirection === 'asc' ? 
                            <ChevronUp className="ml-1 h-3 w-3" /> : 
                            <ChevronDown className="ml-1 h-3 w-3" />
                        )}
                      </Button>
                    </TableHead>
                    
                    {isClient && (
                      <TableHead className="w-32">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 -ml-2 hover:bg-transparent"
                          onClick={() => handleSort('match_score')}
                        >
                          Match Score
                          {sortField === 'match_score' && (
                            sortDirection === 'asc' ? 
                              <ChevronUp className="ml-1 h-3 w-3" /> : 
                              <ChevronDown className="ml-1 h-3 w-3" />
                          )}
                        </Button>
                      </TableHead>
                    )}

                    {isClient && <TableHead className="w-20">Line Items</TableHead>}
                    
                    {canAward && <TableHead className="w-20">Action</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedBids.map((bid) => {
                    const compliance = isClient ? checkVendorCompliance(bid.vendor_profile) : null;
                    const canAwardThis = canAward && compliance?.isCompliant && bid.status === 'submitted';
                    const matchScore = bidMatchScores[bid.id];
                    const lineItems = bidLineItems[bid.id] || [];
                    const isExpanded = expandedLineItems.has(bid.id);

                    return (
                      <Fragment key={bid.id}>
                        <TableRow
                          className={bid.status === 'winner' ? 'bg-green-50 dark:bg-green-950/20 hover:bg-green-100 dark:hover:bg-green-950/30' : 'hover:bg-muted/50'}
                        >
                          {isClient && (
                            <TableCell className="w-24">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="h-8 p-1 text-xs text-primary underline-offset-4 hover:underline max-w-[90px] block"
                                      onClick={() => handleShowVendorSummary(bid)}
                                    >
                                      <span className="truncate">
                                        {getVendorDisplayName(bid.vendor_profile as any)}
                                      </span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{getVendorDisplayName(bid.vendor_profile as any)}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          )}
                          
                          <TableCell className="font-medium w-28 text-sm">
                            <div className="flex items-center gap-2">
                              {(() => {
                                const lineItems = bidLineItems[bid.id] || [];
                                if (lineItems.length === 0) {
                                  return <span className="text-muted-foreground">No items</span>;
                                }
                                
                                // If multiple quantities, show range
                                if (lineItems.length > 1) {
                                  const priceRange = getBidPriceRange(bid.id);
                                  return (
                                    <span>{formatCurrency(priceRange.min)} - {formatCurrency(priceRange.max)}</span>
                                  );
                                } else {
                                  // Single item, show total
                                  return <span>{formatCurrency(calculateBidTotal(bid.id))}</span>;
                                }
                              })()}
                              {isClient && bid.notes && (
                                <MessageSquare className="h-4 w-4 text-blue-500 flex-shrink-0" />
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell className="w-20 text-sm">{getBidLeadTime(bid.id)} days</TableCell>
                          
                          {isClient && (
                            <TableCell className="w-32">
                              {matchScoresLoading ? (
                                <Badge variant="secondary" className="text-xs">Loading...</Badge>
                              ) : matchScore ? (
                                <MatchScoreDisplay matchScore={matchScore} compact={true} />
                              ) : (
                                <span className="text-sm text-muted-foreground">N/A</span>
                              )}
                            </TableCell>
                          )}

                          {isClient && (
                            <TableCell className="w-20">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => toggleLineItems(bid.id)}
                                      className="h-7 px-2 text-xs gap-1"
                                    >
                                      <span>{lineItems.length} items</span>
                                      {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{isExpanded ? 'Hide' : 'View'} bid line items</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          )}
                          
                          {canAward && (
                            <TableCell className="w-20">
                              <Button
                                size="sm"
                                disabled={!canAwardThis || lineItems.length === 0}
                                onClick={() => handleAwardClick(bid)}
                                className="h-8 px-2 text-xs"
                              >
                                Award
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>

                        {/* Inline vendor comment preview */}
                        {isClient && bid.notes && (
                          <TableRow className="hover:bg-transparent border-0">
                            <TableCell colSpan={canAward ? 6 : 5} className="pt-0 pb-2 px-2">
                              <div className="flex items-start gap-2 p-2.5 bg-blue-50 dark:bg-blue-950/30 rounded-md border border-blue-100 dark:border-blue-900 ml-1">
                                <MessageSquare className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5">
                                    Vendor Note
                                  </p>
                                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                    {bid.notes}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                        
                        {isClient && isExpanded && lineItems.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={canAward ? 6 : 5} className="bg-muted/30 p-4">
                              <BidLineItemsView
                                lineItems={lineItems}
                                specifications={specifications.map(s => ({ 
                                  id: s.id, 
                                  name: s.specification_name 
                                }))}
                                quantityOptions={quantityOptions}
                                hideGrandTotal={true}
                                showFulfillmentLocation={true}
                                totalVendorTaxCents={bid.total_vendor_tax_cents || 0}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Award Selection Modal */}
      {selectedBid && showAwardSelectionModal && (
        <AwardSelectionModal
          open={showAwardSelectionModal}
          onOpenChange={(open) => {
            setShowAwardSelectionModal(open);
            if (!open) setSelectedBid(null);
          }}
          jobId={jobId}
          winningBid={selectedBid}
          lineItems={bidLineItems[selectedBid.id] || []}
          specifications={specifications}
          quantityOptions={quantityOptions}
          deliveryLocations={deliveryLocations}
          distributionMatrix={distributionMatrixForAward}
        />
      )}

      {/* Vendor Summary Modal */}
      {selectedVendorData && (
        <VendorSummaryModal
          isOpen={showVendorSummary}
          onClose={() => setShowVendorSummary(false)}
          profile={selectedVendorData.profile}
          certifications={selectedVendorData.certifications}
          capabilities={selectedVendorData.capabilities}
          reviewAggregate={selectedVendorData.reviewAggregate}
          reviews={selectedVendorData.reviews}
        />
      )}
    </div>
  );
};